import { mapChipKeyToStatusTone } from '@/features/shared/utils/tableMetricPresentation';
import { getStatusCopy } from '@/shared/copy/status';
import { getThresholdChipKey } from '@/ui/composites/table/tableUtils';
import { formatDuration } from '@/ui/formatters/formatDuration';
import { formatFrequency } from '@/ui/formatters/formatFrequency';
import { formatPercent } from '@/ui/formatters/formatPercent';
import type { MiniMetricChartSummary } from './MiniMetricChartHeader';
import type { MetricsDto } from '@/features/metrics/metricsHelpers';
import type { MetricThresholdGroup } from '@flowqa/shared';

type MiniMetricSummaryKey = 'passRate' | 'frequency' | 'avgDuration' | 'flakeScore';

const resolvePassRate = (metrics: MetricsDto | null): number | null =>
  metrics?.statusMetrics.find((metric) => metric.key === 'passed')?.percentage ?? null;

const resolveDeltaTone = (
  deltaValue: number | null,
  direction: MetricThresholdGroup[keyof MetricThresholdGroup]['direction'],
): MiniMetricChartSummary['deltaTone'] => {
  if (deltaValue === null || deltaValue === 0) return 'neutral';
  if (direction === 'higherBetter') return deltaValue > 0 ? 'positive' : 'negative';
  return deltaValue < 0 ? 'positive' : 'negative';
};

export const buildMiniMetricChartSummary = ({
  key,
  metrics,
  previousMetrics,
  thresholds,
  showDelta,
}: {
  key: MiniMetricSummaryKey;
  metrics: MetricsDto | null;
  previousMetrics: MetricsDto | null;
  thresholds: MetricThresholdGroup;
  showDelta: boolean;
}): MiniMetricChartSummary => {
  if (key === 'passRate') {
    const value = resolvePassRate(metrics);
    const previousValue = resolvePassRate(previousMetrics);
    const deltaValue = showDelta && value !== null ? value - (previousValue ?? value) : null;

    return {
      valueLabel: value === null ? getStatusCopy('noData') : `${formatPercent(value)}%`,
      valueTone: mapChipKeyToStatusTone(getThresholdChipKey(value, thresholds.passRate)),
      deltaLabel:
        !showDelta || deltaValue === null
          ? null
          : deltaValue === 0
            ? getStatusCopy('noChange')
            : `${deltaValue > 0 ? '+' : ''}${formatPercent(deltaValue)}%`,
      deltaValue,
      deltaTone: resolveDeltaTone(deltaValue, thresholds.passRate.direction),
      isMissing: value === null,
    };
  }

  if (key === 'frequency') {
    const value = metrics?.averages.frequencyPerDay ?? null;
    const previousValue = previousMetrics?.averages.frequencyPerDay ?? null;
    const deltaValue = showDelta && value !== null ? value - (previousValue ?? value) : null;

    return {
      valueLabel: value === null ? getStatusCopy('noData') : formatFrequency(value),
      valueTone: mapChipKeyToStatusTone(getThresholdChipKey(value, thresholds.frequency)),
      deltaLabel:
        !showDelta || deltaValue === null
          ? null
          : deltaValue === 0
            ? getStatusCopy('noChange')
            : `${deltaValue > 0 ? '+' : '-'}${formatFrequency(Math.abs(deltaValue))}`,
      deltaValue,
      deltaTone: resolveDeltaTone(deltaValue, thresholds.frequency.direction),
      isMissing: value === null,
    };
  }

  if (key === 'flakeScore') {
    const value = metrics?.averages.flakeScore ?? null;
    const previousValue = previousMetrics?.averages.flakeScore ?? null;
    const deltaValue = showDelta && value !== null ? value - (previousValue ?? value) : null;

    return {
      valueLabel: value === null ? getStatusCopy('noData') : `${formatPercent(value)}%`,
      valueTone: mapChipKeyToStatusTone(getThresholdChipKey(value, thresholds.flakeScore)),
      deltaLabel:
        !showDelta || deltaValue === null
          ? null
          : deltaValue === 0
            ? getStatusCopy('noChange')
            : `${deltaValue > 0 ? '+' : ''}${formatPercent(deltaValue)}%`,
      deltaValue,
      deltaTone: resolveDeltaTone(deltaValue, thresholds.flakeScore.direction),
      isMissing: value === null,
    };
  }

  const value = metrics?.averages.avgDurationSeconds ?? null;
  const previousValue = previousMetrics?.averages.avgDurationSeconds ?? null;
  const deltaValue = showDelta && value !== null ? value - (previousValue ?? value) : null;

  return {
    valueLabel: value === null ? getStatusCopy('noData') : formatDuration(value * 1000),
    valueTone: mapChipKeyToStatusTone(getThresholdChipKey(value, thresholds.avgDuration)),
    deltaLabel:
      !showDelta || deltaValue === null
        ? null
        : deltaValue === 0
          ? getStatusCopy('noChange')
          : `${deltaValue > 0 ? '+' : '-'}${formatDuration(Math.abs(deltaValue) * 1000, {
              roundMillisecondsWithSeconds: true,
            })}`,
    deltaValue,
    deltaTone: resolveDeltaTone(deltaValue, thresholds.avgDuration.direction),
    isMissing: value === null,
  };
};
